import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { Field, useLeaveGuard } from '../components/Form'
import { Screen } from '../components/Screen'
import { EmptyState, Sheet } from '../components/States'
import { useEnv } from '../context'
import { ageOn, athleteMeta, pluralYears } from '../data/athletes'
import { useGroups, useInvalidateSessionMarks, useSessionBoard } from '../data/hooks'
import { formatDayShort, parseIsoDate, parseSessionKey } from '../data/schedule'
import { SliceApiError, type Athlete } from '../data/types'
import { useOnline } from '../platform/network'

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

/**
 * Новичок прямо на занятии (этап 5, шаг 2; SC04 на перекличке).
 *
 * Минимум: имя, при желании телефон родителя и дата рождения. Спортсмен
 * заводится в группу занятия и сразу получает «Есть» на этом занятии - как
 * «Новенький» прежнего интерфейса. Перед созданием - проверка на дубль по
 * имени на сервере (уменьшительные, порядок слов): «Похоже, такой уже есть» с
 * выбором отметить его, а не завести второго.
 *
 * Создание и отметка - два запроса. Если отметка не прошла, повтор отмечает
 * уже созданного: второй карточки не будет.
 *
 * Разового посещения чужой группы нет: доска занятия показывает только состав
 * группы, а спортсмен ходит в одну группу. Похожий из другой группы - ссылка на
 * карточку, перевести можно там.
 */
/** Проверка на дубль не ответила (не сеть): ничего не создано. */
class SimilarCheckFailed extends Error {}

type Failure = 'offline' | 'server' | 'limit' | 'locked' | 'gone' | 'check'

export function SessionNewcomerScreen() {
  const { key = '' } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { api, now, toast } = useEnv()
  const online = useOnline()
  const parsed = parseSessionKey(key)
  const board = useSessionBoard(key)
  const groups = useGroups()
  const invalidate = useInvalidateSessionMarks(parsed?.groupId)

  const [name, setName] = useState('')
  const [parentPhone, setParentPhone] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [errors, setErrors] = useState<{ name?: string; dateOfBirth?: string }>({})
  const [dupes, setDupes] = useState<Athlete[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<Failure | null>(null)
  // Создан, но «Есть» не сохранилось: повтор только отмечает
  const [created, setCreated] = useState<Athlete | null>(null)
  const inFlight = useRef(false)

  const groupName = board.data?.group_name ?? ''
  const groupNames = useMemo(() => new Map((groups.data ?? []).map((g) => [g.id, g.name])), [groups.data])
  const dirty = !created && !!(name.trim() || parentPhone.trim() || dateOfBirth)
  const leave = useLeaveGuard(dirty)

  useEffect(() => setDupes(null), [name])

  const toSession = useCallback(() => {
    if (location.key !== 'default') navigate(-1)
    else navigate(`/sessions/${encodeURIComponent(key)}`, { replace: true })
  }, [key, location.key, navigate])
  const back = useCallback(() => leave.guard(toSession), [leave, toSession])

  /** Отметить «Есть» и вернуться на занятие. */
  const markPresent = async (a: Athlete, verb: string) => {
    await api.mark(key, a.id, 'present')
    await invalidate()
    toast(`${verb}: ${a.name} - «Есть»`)
    toSession()
  }

  const describe = (err: unknown): Failure => {
    if (err instanceof SimilarCheckFailed) return 'check'
    if (!(err instanceof SliceApiError) || err.kind === 'network') return 'offline'
    if (err.kind === 'limit') return err.message === 'athlete_limit_reached' ? 'limit' : 'locked'
    if (err.kind === 'not_found') return 'gone'
    return 'server'
  }

  const run = async (action: () => Promise<void>) => {
    if (inFlight.current) return
    inFlight.current = true
    setBusy(true)
    setFailure(null)
    try {
      await action()
    } catch (err) {
      setFailure(describe(err))
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  const submit = (skipDupes: boolean) => {
    if (created) {
      void run(() => markPresent(created, 'Добавлено'))
      return
    }
    const e: typeof errors = {}
    if (!name.trim()) e.name = 'Укажите имя и фамилию'
    if (dateOfBirth && !DATE_RE.test(dateOfBirth)) e.dateOfBirth = 'Дата в формате ГГГГ-ММ-ДД'
    setErrors(e)
    if (Object.keys(e).length > 0) return

    setDupes(null)
    // Проверка и создание - один полёт: второе нажатие ничего не шлёт.
    void run(async () => {
      if (!skipDupes) {
        let found: Athlete[]
        try {
          found = await api.findSimilarAthletes(name.trim())
        } catch (err) {
          if (err instanceof SliceApiError && err.kind === 'network') throw err
          throw new SimilarCheckFailed()
        }
        if (found.length > 0) {
          setDupes(found)
          return
        }
      }
      let a: Athlete
      try {
        a = await api.createAthlete({
          name: name.trim(),
          groupId: parsed!.groupId,
          dateOfBirth: dateOfBirth || undefined,
          parentPhone: parentPhone.trim() || undefined,
        })
      } catch (err) {
        // Ответ мог потеряться после создания: повтор проверит на дубль на
        // сервере и покажет созданного как «похоже, такой уже есть».
        if (err instanceof SliceApiError && err.kind === 'network') void invalidate()
        throw err
      }
      setCreated(a)
      await markPresent(a, 'Добавлено')
    })
  }

  const pickExisting = (a: Athlete) => {
    setDupes(null)
    void run(async () => {
      if (!a.group_id) {
        // Без группы: в группу занятия, иначе на доске его не будет
        await api.updateAthlete(a.id, { group_id: parsed!.groupId })
        await markPresent(a, 'В группе и отмечено')
        return
      }
      await markPresent(a, 'Отмечено')
    })
  }

  const date = parsed ? parseIsoDate(parsed.date) : null
  const subtitle = board.data && date ? `${groupName} / ${formatDayShort(date)}${parsed?.startTime ? ` / ${parsed.startTime}` : ''}` : undefined

  if (!parsed) {
    return (
      <Screen title="Новичок" back={toSession}>
        <EmptyState title="Ссылка на занятие устарела или неверна" text="Откройте занятие из расписания." />
      </Screen>
    )
  }

  return (
    <Screen
      title="Новичок"
      subtitle={subtitle}
      back={back}
      actionBar={
        <button
          type="button"
          className="pc-btn pc-btn--primary pc-btn--block"
          disabled={busy || board.data?.cancelled}
          onClick={() => submit(false)}
        >
          {busy ? 'Сохраняю…' : created ? 'Повторить отметку «Есть»' : 'Добавить и отметить «Есть»'}
        </button>
      }
    >
      {board.data?.cancelled ? (
        <EmptyState title="Занятие отменено" text="На отменённое занятие новичка не отметить." />
      ) : (
        <form
          className="pc-form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault()
            submit(false)
          }}
        >
          <p className="pc-muted" style={{ margin: '12px 0 0', fontSize: 14 }}>
            {groupName ? `Новичок попадёт в группу «${groupName}» и сразу получит «Есть» на этом занятии.` : 'Новичок попадёт в группу занятия и сразу получит «Есть».'}{' '}
            Остальное можно заполнить в карточке позже.
          </p>

          {created && failure && (
            <div className="pc-alert" role="alert">
              {failure === 'offline' || !online
                ? `Нет сети - «Есть» не сохранено. ${created.name} уже в группе, повтор только отметит.`
                : `«Есть» не сохранено. ${created.name} уже в группе, повтор только отметит.`}
            </div>
          )}
          {!created && failure && <FailureNote kind={failure} onSkipCheck={() => submit(true)} />}

          <Field label="Имя и фамилия" error={errors.name}>
            {(p) => (
              <input
                {...p}
                className="pc-input"
                value={name}
                disabled={!!created}
                onChange={(e) => setName(e.target.value)}
                autoComplete="off"
              />
            )}
          </Field>
          <Field label="Телефон родителя" hint="Можно не указывать">
            {(p) => (
              <input
                {...p}
                className="pc-input"
                type="tel"
                value={parentPhone}
                disabled={!!created}
                onChange={(e) => setParentPhone(e.target.value)}
              />
            )}
          </Field>
          <Field label="Дата рождения" error={errors.dateOfBirth} hint="Можно не указывать">
            {(p) => (
              <input
                {...p}
                className="pc-input"
                type="date"
                value={dateOfBirth}
                disabled={!!created}
                onChange={(e) => setDateOfBirth(e.target.value)}
              />
            )}
          </Field>
        </form>
      )}

      {dupes && (
        <Sheet title="Похоже, такой уже есть" onClose={() => setDupes(null)}>
          <p className="pc-muted" style={{ margin: '0 0 8px', fontSize: 14 }}>
            Проверьте, не тот же ли это человек. Если тот же - отметьте его, а не заводите второго.
          </p>
          <ul className="pc-list" style={{ marginBottom: 12 }}>
            {dupes.map((d) => {
              const inThisGroup = d.group_id === parsed.groupId
              const otherGroup = !!d.group_id && !inThisGroup
              return (
                <li key={d.id} className="pc-change">
                  <span className="pc-change__who">{d.name}</span>
                  <span className="pc-change__what">
                    {inThisGroup ? `В этой группе${ageSuffix(d, now())}` : athleteMeta(d, d.group_id ? groupNames.get(d.group_id) : undefined, now())}
                  </span>
                  {inThisGroup && (
                    <button type="button" className="pc-btn pc-btn--block" onClick={() => pickExisting(d)}>
                      Тот же человек - отметить «Есть»
                    </button>
                  )}
                  {!d.group_id && (
                    <button type="button" className="pc-btn pc-btn--block" onClick={() => pickExisting(d)}>
                      Тот же человек - в группу и «Есть»
                    </button>
                  )}
                  {otherGroup && (
                    <>
                      <span className="pc-change__what">
                        Отметить на занятии другой группы нельзя: спортсмен ходит в одну группу. Перевести можно в карточке.
                      </span>
                      <Link className="pc-btn pc-btn--block" to={`/athletes/${d.id}`}>
                        Открыть карточку
                      </Link>
                    </>
                  )}
                </li>
              )
            })}
          </ul>
          <button type="button" className="pc-btn pc-btn--block pc-btn--primary" onClick={() => submit(true)}>
            Это другой человек, добавить
          </button>
          <button type="button" className="pc-btn pc-btn--block" onClick={() => setDupes(null)}>
            Отмена
          </button>
        </Sheet>
      )}
      {leave.dialog}
    </Screen>
  )
}

function ageSuffix(a: Athlete, now: Date): string {
  const age = ageOn(a.date_of_birth, now)
  return age === null ? '' : `, ${pluralYears(age)}`
}

function FailureNote({ kind, onSkipCheck }: { kind: Failure; onSkipCheck: () => void }) {
  if (kind === 'check') {
    return (
      <div className="pc-alert" role="alert">
        <span style={{ flex: 1 }}>Не удалось проверить, нет ли такого уже. Ничего не создано: можно повторить или добавить без проверки.</span>
        <button type="button" className="pc-btn" onClick={onSkipCheck}>
          Добавить без проверки
        </button>
      </div>
    )
  }
  if (kind === 'limit') {
    return (
      <div className="pc-alert" role="alert">
        <span style={{ flex: 1 }}>Тариф клуба не даёт добавить ещё спортсмена: лимит спортсменов исчерпан. Ничего не создано.</span>
        <Link className="pc-btn" to="/club/subscription">
          Подписка клуба
        </Link>
      </div>
    )
  }
  if (kind === 'locked') {
    return (
      <div className="pc-alert" role="alert">
        <span style={{ flex: 1 }}>Подписка клуба закончилась: клуб только для чтения, новичка не добавить.</span>
        <Link className="pc-btn" to="/club/subscription">
          Подписка клуба
        </Link>
      </div>
    )
  }
  if (kind === 'gone') {
    return (
      <div className="pc-alert" role="alert">
        Занятие или группа не найдены: возможно, группу удалили.
      </div>
    )
  }
  return (
    <div className="pc-alert" role="alert">
      {kind === 'offline' ? 'Нет сети - не сохранено.' : 'Не сохранилось.'} Данные остались на экране, попробуйте ещё раз.
    </div>
  )
}
